/**
 * Bildefrekvenser og tidskodespesifikasjon.
 *
 * `nominal` er tallet tidskoden teller i — 30 for 29,97. `num/den` er den
 * faktiske frekvensen og er det eneste som brukes når forløpt tid regnes om
 * til frames. Forskjellen mellom de to er hele grunnen til at drop-frame
 * finnes, og de skal aldri blandes.
 */

export type FramerateName =
  | "23.976"
  | "24"
  | "25"
  | "29.97"
  | "30"
  | "50"
  | "59.94"
  | "60";

export interface Framerate {
  readonly name: FramerateName;
  /** Tallet tidskoden teller i. 30 for 29,97. */
  readonly nominal: number;
  /** Faktisk frekvens som brøk: num/den frames per sekund. */
  readonly num: number;
  readonly den: number;
  /** Om drop-frame er definert for frekvensen i det hele tatt. */
  readonly supportsDropFrame: boolean;
}

const RATES: { readonly [K in FramerateName]: Framerate } = {
  "23.976": { name: "23.976", nominal: 24, num: 24000, den: 1001, supportsDropFrame: false },
  "24": { name: "24", nominal: 24, num: 24, den: 1, supportsDropFrame: false },
  "25": { name: "25", nominal: 25, num: 25, den: 1, supportsDropFrame: false },
  "29.97": { name: "29.97", nominal: 30, num: 30000, den: 1001, supportsDropFrame: true },
  "30": { name: "30", nominal: 30, num: 30, den: 1, supportsDropFrame: false },
  "50": { name: "50", nominal: 50, num: 50, den: 1, supportsDropFrame: false },
  "59.94": { name: "59.94", nominal: 60, num: 60000, den: 1001, supportsDropFrame: true },
  "60": { name: "60", nominal: 60, num: 60, den: 1, supportsDropFrame: false },
};

export const FRAMERATE_NAMES = Object.keys(RATES) as readonly FramerateName[];

export function framerate(name: FramerateName): Framerate {
  return RATES[name];
}

export function isFramerateName(v: string): v is FramerateName {
  return Object.prototype.hasOwnProperty.call(RATES, v);
}

/**
 * En tidskodespesifikasjon: frekvens pluss om den telles med drop-frame.
 * Alle regnefunksjoner tar denne, aldri en løs frekvens.
 */
export interface TimecodeSpec {
  readonly rate: Framerate;
  readonly dropFrame: boolean;
  /** Antall framenumre som hoppes over per minutt. 0 uten drop-frame. */
  readonly dropCount: number;
  /** Frames i ett døgn, i denne tellingen. */
  readonly framesPerDay: number;
  /** Frames i ti minutter. Grunnenheten i drop-frame-regnestykket. */
  readonly framesPer10Min: number;
  /** Frames i ett minutt, etter at de droppede er trukket fra. */
  readonly framesPerMin: number;
}

export class TimecodeError extends Error {
  override readonly name = "TimecodeError";
}

export function timecodeSpec(name: FramerateName, dropFrame: boolean): TimecodeSpec {
  const rate = RATES[name];
  if (dropFrame && !rate.supportsDropFrame) {
    throw new TimecodeError(`Drop-frame finnes ikke for ${name} fps`);
  }
  // 2 for 30-tellingen, 4 for 60-tellingen.
  const dropCount = dropFrame ? Math.round(rate.nominal * 0.066666) : 0;
  const framesPer10Min = dropFrame
    ? Math.round((rate.nominal * 1000) / 1001 * 600)
    : rate.nominal * 600;
  const framesPerHour = dropFrame
    ? Math.round((rate.nominal * 1000) / 1001 * 3600)
    : rate.nominal * 3600;
  return {
    rate,
    dropFrame,
    dropCount,
    framesPerDay: framesPerHour * 24,
    framesPer10Min,
    framesPerMin: rate.nominal * 60 - dropCount,
  };
}
